import { useState, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { marketService } from '../../services/marketService';
import { Market } from '../../types/market';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorAlert from '../../components/ui/ErrorAlert';
import StatsCard from '../../components/ui/StatsCard';
import ConfirmModal from '../../components/ui/ConfirmModal';

const EMPTY_FORM = {
  title: '',
  description: '',
  linkUrl: '',
  imageUrl: '',
  isVisible: true,
  sortOrder: 0,
};

function safeUrl(url: string): string {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

export default function MarketList() {
  const queryClient = useQueryClient();

  const { data: markets = [], isLoading, error } = useQuery({
    queryKey: ['markets'],
    queryFn: marketService.getAllMarkets,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'hidden'>('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Market | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Market | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['markets'] });

  const createMutation = useMutation({
    mutationFn: ({ data, file }: { data: typeof EMPTY_FORM; file?: File }) =>
      marketService.createMarket(data, file ?? undefined),
    onSuccess: () => { invalidate(); closeModal(); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data, file }: { id: number; data: typeof EMPTY_FORM; file?: File }) =>
      marketService.updateMarket(id, data, file ?? undefined),
    onSuccess: () => { invalidate(); closeModal(); },
  });

  const deleteMutation = useMutation({
    mutationFn: marketService.deleteMarket,
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
  });

  const toggleMutation = useMutation({
    mutationFn: (item: Market) =>
      marketService.updateMarket(item.id, { ...item, isVisible: !item.isVisible }),
    onSuccess: () => invalidate(),
  });

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM, sortOrder: markets.length });
    setImageFile(null);
    setImagePreview(null);
    setModalOpen(true);
  };

  const openEdit = (item: Market) => {
    setEditTarget(item);
    setForm({
      title: item.title ?? '',
      description: item.description ?? '',
      linkUrl: item.linkUrl ?? '',
      imageUrl: item.imageUrl ?? '',
      isVisible: item.isVisible,
      sortOrder: item.sortOrder,
    });
    setImageFile(null);
    setImagePreview(item.imageUrl ?? null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = () => {
    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, data: form, file: imageFile ?? undefined });
    } else {
      createMutation.mutate({ data: form, file: imageFile ?? undefined });
    }
  };

  const totalCount   = markets.length;
  const visibleCount = markets.filter((m) => m.isVisible).length;
  const hiddenCount  = markets.filter((m) => !m.isVisible).length;
  const isPending    = createMutation.isPending || updateMutation.isPending;

  const filteredMarkets = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return [...markets]
      .filter((m) => {
        const matchesSearch =
          (m.title ?? '').toLowerCase().includes(term) ||
          (m.description ?? '').toLowerCase().includes(term) ||
          (m.linkUrl ?? '').toLowerCase().includes(term);
        const matchesStatus =
          filterStatus === 'all' ||
          (filterStatus === 'active' && m.isVisible) ||
          (filterStatus === 'hidden' && !m.isVisible);
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [markets, searchTerm, filterStatus]);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert />;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">마켓 관리</h1>
          <p className="text-gray-500 mt-2">앱 마켓 항목을 관리합니다</p>
        </div>
        <button
          onClick={openCreate}
          className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all shadow-md flex items-center space-x-2"
        >
          <i className="fas fa-plus" />
          <span>항목 추가</span>
        </button>
      </div>

      {/* 검색 & 필터 */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 relative">
            <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="제목, 설명, 링크로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
            />
          </div>
          <div className="flex gap-2">
            {([
              { key: 'all',    label: '전체' },
              { key: 'active', label: '활성화' },
              { key: 'hidden', label: '비활성화' },
            ] as const).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  filterStatus === key
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          총 <span className="font-semibold text-primary">{filteredMarkets.length}</span>개 항목
          {searchTerm || filterStatus !== 'all' ? ` (전체 ${totalCount}개 중)` : ''}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatsCard label="전체" value={totalCount} icon="fas fa-store" borderColor="border-primary" iconColor="text-primary" bgColor="bg-primary/10" />
        <StatsCard label="활성화" value={visibleCount} icon="fas fa-eye" borderColor="border-green-500" iconColor="text-green-500" bgColor="bg-green-500/10" valueColor="text-green-600" />
        <StatsCard label="비활성화" value={hiddenCount} icon="fas fa-eye-slash" borderColor="border-gray-400" iconColor="text-gray-400" bgColor="bg-gray-400/10" valueColor="text-gray-600" />
      </div>

      {/* 카드 목록 */}
      {filteredMarkets.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-16 text-center">
          <i className="fas fa-store text-8xl mb-6 text-gray-200" />
          <p className="text-xl font-semibold text-gray-600 mb-2">
            {searchTerm || filterStatus !== 'all' ? '검색 결과가 없습니다' : '등록된 마켓 항목이 없습니다'}
          </p>
          <p className="text-gray-400">
            {searchTerm || filterStatus !== 'all' ? '다른 검색어나 필터를 시도해보세요' : '항목 추가 버튼을 눌러 첫 번째 항목을 만들어보세요'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {filteredMarkets.map((item) => (
            <div
              key={item.id}
              className={`bg-white rounded-xl shadow-md overflow-hidden border transition-all ${
                item.isVisible ? 'border-gray-100' : 'border-gray-200 opacity-60'
              }`}
            >
              {/* 이미지 */}
              <div className="relative h-44 bg-gray-50">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.title ?? ''} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="fas fa-image text-4xl text-gray-200" />
                  </div>
                )}
                {/* 활성화 배지 */}
                <span className={`absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded-full ${
                  item.isVisible ? 'bg-primary text-white' : 'bg-gray-400 text-white'
                }`}>
                  {item.isVisible ? '활성화' : '비활성화'}
                </span>
              </div>

              {/* 내용 */}
              <div className="p-4">
                <h3 className="font-bold text-gray-800 text-base mb-1 truncate">{item.title}</h3>
                {item.description && (
                  <p className="text-sm text-gray-500 line-clamp-2 mb-2">{item.description}</p>
                )}
                {item.linkUrl && (
                  <a
                    href={safeUrl(item.linkUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1 mb-3 truncate"
                  >
                    <i className="fas fa-external-link-alt" />
                    {item.linkUrl}
                  </a>
                )}

                {/* 버튼 */}
                <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => toggleMutation.mutate(item)}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      item.isVisible
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-primary/10 text-primary hover:bg-primary/20'
                    }`}
                  >
                    <i className={`fas fa-${item.isVisible ? 'eye-slash' : 'eye'} mr-1`} />
                    {item.isVisible ? '비활성화' : '활성화'}
                  </button>
                  <button
                    onClick={() => openEdit(item)}
                    className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    <i className="fas fa-pen mr-1" />편집
                  </button>
                  <button
                    onClick={() => setDeleteTarget(item)}
                    className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-text-red/10 text-text-red hover:bg-text-red/20 transition-colors"
                  >
                    <i className="fas fa-trash mr-1" />삭제
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 추가/편집 모달 */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  {editTarget ? '마켓 항목 수정' : '마켓 항목 추가'}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">제목은 필수입니다.</p>
              </div>
              <button onClick={closeModal} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                <i className="fas fa-times text-lg" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">

              {/* 이미지 업로드 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  배너 이미지 <span className="text-gray-400 font-normal">(선택)</span>
                </label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="relative h-36 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all overflow-hidden"
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="미리보기" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <i className="fas fa-cloud-upload-alt text-3xl text-gray-300 mb-2" />
                      <p className="text-xs text-gray-400">클릭하여 이미지 업로드</p>
                      <p className="text-xs text-gray-300 mt-0.5">JPG, PNG 권장</p>
                    </div>
                  )}
                  {imagePreview && (
                    <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium">이미지 변경</span>
                    </div>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </div>

              {/* 제목 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  제목 <span className="text-text-red">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="마켓 항목 제목"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
              </div>

              {/* 설명 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  설명 <span className="text-gray-400 font-normal">(선택)</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="마켓 항목에 대한 설명을 입력하세요"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
                />
              </div>

              {/* 링크 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  웹사이트 링크 <span className="text-gray-400 font-normal">(선택)</span>
                </label>
                <input
                  type="url"
                  value={form.linkUrl}
                  onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
                  placeholder="https://example.com"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                />
              </div>

              {/* 활성화 여부 */}
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-semibold text-gray-700">활성화</p>
                  <p className="text-xs text-gray-400 mt-0.5">앱에서 이 항목을 표시합니다</p>
                </div>
                <button
                  onClick={() => setForm({ ...form, isVisible: !form.isVisible })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.isVisible ? 'bg-primary' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    form.isVisible ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50/50">
              <button onClick={closeModal} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                취소
              </button>
              <button
                onClick={handleSave}
                disabled={!form.title.trim() || isPending}
                className="px-5 py-2 text-sm text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isPending ? (
                  <><i className="fas fa-circle-notch fa-spin" /><span>저장 중...</span></>
                ) : (
                  <><i className="fas fa-check" /><span>{editTarget ? '수정 완료' : '추가'}</span></>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {deleteTarget && (
        <ConfirmModal
          title={`"${deleteTarget.title}" 항목을 삭제하시겠습니까?`}
          description="삭제된 항목은 복구할 수 없습니다."
          confirmLabel="삭제"
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
